import { Incoterm } from '../../../../../current/types'
import { LabelRate } from '../../../../../types/api'
import { DeliveryConfirmation, HazmatReason, SpecialRates } from './PackageConstants'

export interface Parcel {
  length?: number | string // these are initialized as numbers, then become strings whenever the user types into the numeric inputs (controller components use strings for typing)
  width?: number | string
  height?: number | string
  weight?: number | string
  box?: number | string // initialized as a number, then becomes a string when changing via select dropdown
  shippingBoxId?: number
  predefinedPackage?: string
  declaredValue?: number
}

export interface BoxOptions {
  label: string
  value: string
}

export interface ShipmentOptions {
  deliveryConfirmation?: DeliveryConfirmation
  specialRatesEligibility?: SpecialRates
  hazmat?: HazmatReason
  labelDate?: Date
  printCustom_1?: string
  printCustom_2?: string
  incoterm?: Incoterm
  carrierInsuranceAmount?: number
  // EasyPost: 0 bypasses USPS address validation, 1 (or undefined) validates normally
  addressValidationLevel?: 0 | 1
}

export interface RankableLabelRate extends LabelRate {
  fastest?: boolean
  cheapest?: boolean
}

export interface UsaStateInfo {
  name: string
  abbreviation: string
}

export interface CountryOption {
  name: string
  abbreviation: string
}

export type RateHandler = (RankableLabelRate) => void

interface ShipmentRateOptions {
  rates: RankableLabelRate[]
  error?: any
  externalOrderId?: string
}

export interface ShipmentRateResponse {
  data: ShipmentRateOptions
}

export interface ShipmentParcel {
  parcel: Parcel
  options: ShipmentOptions
  parcelNumber: number
  packerId?: number
  declaredValue?: number
}

export enum CustomsInfoContentsType {
  'Returned Goods' = 'returned_goods',
  Documents = 'documents',
  Gift = 'gift',
  Merchandise = 'merchandise',
  // Other = 'other', triggers an input box, think 99.99% of everything is merchandise
  Sample = 'sample'
}
export enum CustomsInfoRestrictionType {
  'Sanitary Phytosanitary Inspection' = 'sanitary_phytosanitary_inspection',
  None = 'none',
  // Other = 'other', triggers an input box, think 99.99% of everything wont require this
  Quarantine = 'quarantine'
}
export enum CustomsInfoNonDeliveryOption {
  Abandon = 'abandon',
  Return = 'return'
}

export interface CustomsInfo {
  contentsType: CustomsInfoContentsType
  // only required if contentsTyoe is other
  contentsExplanation?: string
  restrictionType: CustomsInfoRestrictionType
  // only required if restrictionType is other
  restrictionComments?: string
  customsCertify: boolean
  customsSigner: string
  nonDeliveryOption: CustomsInfoNonDeliveryOption
  eelPfc: string
  customsItems: CustomsInfoItem[]
}

export interface CustomsInfoItem {
  /**
   * A brief description of the item
   */
  description: string

  /**
   * Number of that item contained in the package
   */
  quantity: number

  /**
   * Total weight in ounces of all the items of that type in the package
   */
  weight: number | string

  /**
   * Total value in US dollars of all the items of that type in the package
   */
  value: number | string

  /**
   * The six digit code for your item as specified by harmonized system for tariffs. We talk a bit more about this below.
   * This is optional https://support.goshippo.com/hc/en-us/articles/213105123-Using-Tariff-Numbers-and-Harmonized-Codes
   */
  hsTariffNumber?: number | string

  /**
   * Where the item was manufactured or assembled.
   */
  originCountry: string

  /**
   * For multi-parcel shipments, we need to track which item is in which parcel.
   */
  parcelNumber?: number
}
